import React from 'react';

import { ButtonGroup, FormField, FormHelp, Label, ToggleButton } from '@zillow/constellation';

import type { Section } from '../../../models';
import { RADON_HAZARD } from '../../../constants';
import YesAndNo from '../../Widgets/YesAndNo';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function RadonHazard({ section, setSectionValues }: Props): JSX.Element {
  function onNoTestClick(): void {
    setSectionValues({
      [RADON_HAZARD.FIELD_NAME.NOT_PRESENT]: true,
      [RADON_HAZARD.FIELD_NAME.WITH_REPORTS]: true,
      [RADON_HAZARD.FIELD_NAME.PRESENT]: false,
      [RADON_HAZARD.FIELD_NAME.REMEDIATED]: false,
      [RADON_HAZARD.FIELD_NAME.HAS_RELATED_REPORTS]: null,
    });
  }

  function onPresentClick(): void {
    setSectionValues({
      [RADON_HAZARD.FIELD_NAME.NOT_PRESENT]: false,
      [RADON_HAZARD.FIELD_NAME.WITH_REPORTS]: true,
      [RADON_HAZARD.FIELD_NAME.PRESENT]: true,
      [RADON_HAZARD.FIELD_NAME.REMEDIATED]: false,
    });
  }

  function onRemediatedClick(): void {
    setSectionValues({
      [RADON_HAZARD.FIELD_NAME.NOT_PRESENT]: false,
      [RADON_HAZARD.FIELD_NAME.WITH_REPORTS]: false,
      [RADON_HAZARD.FIELD_NAME.PRESENT]: false,
      [RADON_HAZARD.FIELD_NAME.REMEDIATED]: true,
      [RADON_HAZARD.FIELD_NAME.HAS_RELATED_REPORTS]: null,
    });
  }

  function onYesReports(): void {
    setSectionValues({
      [RADON_HAZARD.FIELD_NAME.HAS_RELATED_REPORTS]: true,
    });
  }

  function onNoReports(): void {
    setSectionValues({
      [RADON_HAZARD.FIELD_NAME.HAS_RELATED_REPORTS]: false,
    });
  }

  const radonNotPresent = section.getFieldValue(RADON_HAZARD.FIELD_NAME.NOT_PRESENT);
  const radonPresent = section.getFieldValue(RADON_HAZARD.FIELD_NAME.PRESENT);
  const radonRemediated = section.getFieldValue(RADON_HAZARD.FIELD_NAME.REMEDIATED);

  return (
    <>
      <FormField
        marginBottom="sm"
        label={<Label>Do you know of any test that indicated the presence of unsafe levels of radon?</Label>}
        control={
          <ButtonGroup
            aria-label="radon hazard types"
            data-test="radon-button-group"
            fluid
            direction="column"
            conjoined
          >
            <ToggleButton
              aria-label={RADON_HAZARD.LABELS.NO_TEST}
              data-test="no-btn"
              pressed={radonNotPresent}
              onClick={onNoTestClick}
            >
              {RADON_HAZARD.LABELS.NO_TEST}
            </ToggleButton>
            <ToggleButton
              aria-label={RADON_HAZARD.LABELS.PRESENT}
              data-test="yes-present-btn"
              pressed={radonPresent}
              onClick={onPresentClick}
            >
              {RADON_HAZARD.LABELS.PRESENT}
            </ToggleButton>
            <ToggleButton
              aria-label={RADON_HAZARD.LABELS.REMEDIATED}
              data-test="yes-remediated-btn"
              pressed={radonRemediated}
              onClick={onRemediatedClick}
            >
              {RADON_HAZARD.LABELS.REMEDIATED}
            </ToggleButton>
          </ButtonGroup>
        }
        description={
          <FormHelp>Radon levels of 4 picocuries-per-liter and higher are considered unsafe by the EPA</FormHelp>
        }
      />
      {radonPresent && (
        <FormField
          label={<Label>Do you have any reports related to radon at the property?</Label>}
          control={
            <YesAndNo
              yesHandler={onYesReports}
              noHandler={onNoReports}
              yesActive={section.getFieldValue(RADON_HAZARD.FIELD_NAME.HAS_RELATED_REPORTS)}
            />
          }
          description={
            <FormHelp>
              You must provide copies of any reports before the lease is signed by tenant; later on, you will have the
              ability to attach any reports to your lease
            </FormHelp>
          }
        />
      )}
    </>
  );
}
